import { Box, CircularProgress, Tab, Tabs, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useNavigate, useLocation, createFileRoute } from "@tanstack/react-router";

import CategorySelection from "../../pages/protected/home/components/CategorySelection";
import OrganizationSelection from "../../pages/protected/home/components/OrganizationSelection";
import ProcessList from "../../pages/protected/home/components/ProcessList";
import { getCategoriesProcess } from "../../api/getProcess";
import { getOrganizations } from "../../api/organizations";
import { getRequestsList } from "../../api/tasks";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import RequestsAccordion from "../../components/RequestsAccordion";
import ScrollToTopButton from "../../components/ScrollToTopButton";
import useQueryString from "../../hooks/useQueryString";
import "../../pages/protected/home/styles.css";
import isSesi from "../../utils/envUtils";

interface ProcessDefinitions {
  id: string;
  name: string;
  key: string;
  version: number;
  startable: boolean;
  suspended: boolean;
  resource: string;
  deploymentId: string;
  tenantId: string;
  category: string;
  description: string;
  isExecutavelPortalExterno: boolean;
}

interface CategorieProcess {
  category: string;
  processDefinitions: ProcessDefinitions[];
}

declare global {
  interface Window {
    versionSystem?: string;
  }
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function CustomTabPanel(props: TabPanelProps): JSX.Element {
  const { children, value, index, ...other } = props;

  return (
    <div
      aria-labelledby={`simple-tab-${index}`}
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      role="tabpanel"
      {...other}
    >
      {value === index && <Box sx={{ p: 0 }}>{children}</Box>}
    </div>
  );
}

function HomePage(): React.JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { processId, updatePage } = useQueryString<{
    processId: string;
    updatePage: string;
  }>();
  const { data: myRequests, error: requestsError } = useQuery({
    queryKey: ["requests-list"],
    queryFn: async () => (await getRequestsList()) as unknown as Pagination<Requests>,
    retry: false,
  });

  useEffect(() => {
    if (requestsError) {
      navigate({ to: "/error" });
    }
  }, [requestsError, navigate]);
  const [categoriesProcess, setCategoriesProcess] = useState<
    CategorieProcess[] | null
  >(null);
  const [organizations, setOrganizations] = useState<Organization[] | null>(
    null
  );
  const [selectedOrganization, setSelectedOrganization] =
    useState<Organization | null>(null);
  const [selectCategory, setSelectCategory] = useState<CategorieProcess | null>(
    null
  );
  const [valueTab, setValueTab] = useState(0);
  const [isViewTabs, setIsViewTabs] = useState(true);

  const getOrganizationsFunction = async (): Promise<void> => {
    try {
      const { data } = await getOrganizations();
      if (data.length > 0) {
        if(isSesi()){
          // exclui a organização "sesi"
          const editedData = data.filter(o => o.id !== "sesi");
          setOrganizations(editedData);
        } else{
          setOrganizations(data);
        }
      } else void getProcess();
    } catch (error) {
      navigate({ to: "/error" });
    }
  };

  useEffect(() => {
    if (!processId) {
      window.scrollTo(0, 0);
    }
    void getOrganizationsFunction();
  }, [processId]);

  useEffect(() => {
    if (updatePage) {
      setSelectCategory(null);
      setSelectedOrganization(null);
      void navigate({ to: location.pathname as any, replace: true });
    }
  }, [updatePage, location.pathname, navigate]);

  const getProcess = async (tenantId?: string): Promise<void> => {
    try {
      const { data } = await getCategoriesProcess(tenantId);
      setCategoriesProcess(data);
    } catch (error) {
      navigate({ to: "/error" });
    }
  };

  const handleChange = (
    _event: React.SyntheticEvent,
    newValue: number
  ): void => {
    setValueTab(newValue);
  };

  useEffect(() => {
    if (selectedOrganization) {
      setIsViewTabs(false);
      void getProcess(selectedOrganization.id);
    }
  }, [selectedOrganization]);

  const handleNavigateToHome = (): void => {
    setIsViewTabs(true);
    setValueTab(0);
    setSelectCategory(null);
    setSelectedOrganization(null);
  };

  return (
    <Box className="container">
      <ScrollToTopButton />
      {isViewTabs ? (
        <>
          <Typography
            sx={{
              fontSize: { xs: "24px", md: "28px" }, // Responsive font size
              fontWeight: 500,
              marginLeft: "16px",
            }}
          >
            {window.constants?.titleHome || "Smartdocs Portal"}
          </Typography>
          <Box sx={{ borderBottom: 1, borderColor: "divider", width: "100%" }}>
            <Tabs onChange={handleChange} value={valueTab} variant="fullWidth">
              <Tab id="catalog" label={t("home.catalog")} sx={{ width: "50% " }} />
              <Tab id="requests" label={t("home.requests")} sx={{ width: "50%" }} />
            </Tabs>
          </Box>
          <CustomTabPanel index={0} value={valueTab}>
            {(() => {
              if (organizations && !selectedOrganization) {
                return (
                  <OrganizationSelection
                    organizations={organizations}
                    setSelectedOrganization={setSelectedOrganization}
                  />
                );
              }
              if (categoriesProcess && !selectCategory) {
                return (
                  <CategorySelection
                    categoriesProcess={categoriesProcess}
                    isViewBreadcrumbs={false}
                    setCategory={setSelectCategory}
                  />
                );
              }
              if (selectCategory) {
                return (
                  <ProcessList
                    isViewBreadcrumbs={false}
                    navigate={(path: string) => {
                      void navigate({ to: path as any });
                    }}
                    selectCategory={selectCategory}
                    setCategory={setSelectCategory}
                  />
                );
              }
              return (
                <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
                  <CircularProgress />
                </Box>
              );
            })()}
          </CustomTabPanel>
          <CustomTabPanel index={1} value={valueTab}>
            {selectCategory === null && myRequests ? (
              <RequestsAccordion
                myRequests={myRequests}
                processId={processId}
              />
            ) : null}
          </CustomTabPanel>
        </>
      ) : (
        <>
          {(() => {
            if (categoriesProcess && !selectCategory) {
              return (
                <CategorySelection
                  categoriesProcess={categoriesProcess}
                  isViewBreadcrumbs
                  navigateToHome={handleNavigateToHome}
                  setCategory={setSelectCategory}
                  tenantName={selectedOrganization?.name}
                />
              );
            }
            if (selectCategory) {
              return (
                <ProcessList
                  isViewBreadcrumbs
                  navigate={(path: string) => {
                    void navigate({ to: path as any });
                  }}
                  navigateToHome={handleNavigateToHome}
                  selectCategory={selectCategory}
                  setCategory={setSelectCategory}
                  tenantName={selectedOrganization?.name}
                />
              );
            }
            return (
              <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
                <CircularProgress />
              </Box>
            );
          })()}
        </>
      )}
    </Box>
  );
}

export const Route = createFileRoute("/__auth/")({
  component: HomePage,
});


